import React, { useContext, useMemo } from "react";
import { ArAgingSummaryResponse } from "@common/types/reports/types";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import {
    ArAgingDetailDrillDownParams,
    ArAgingSummaryTableMeta,
} from "./arAgingDetailDrillDown.lib";
import { buildArAgingSummaryRows } from "./arAgingSummaryViewBuilder";
import {
    buildArAgingSummaryConfig,
    buildArAgingSummaryView,
} from "./arAgingSummaryViewConfig";
import styles from "./ArAgingSummaryTable.module.scss";

interface Props {
    data: ArAgingSummaryResponse;
    onDrillDown: (params: ArAgingDetailDrillDownParams) => void;
}

/**
 * A/R Aging Summary table rendered via the shared ViewTable runtime.
 * - Columns are fixed (Customer + the 5 AR_AGING_BUCKET_ORDER buckets + Total)
 * - Flat data; no nesting / expand-collapse
 * - No sort support (unlike Expense by Vendor) \u2014 rows arrive pre-sorted
 *   from the backend (A\u2192Z by customer name, "No customer" last).
 */
export const ArAgingSummaryTable: React.FC<Props> = ({ data, onDrillDown }) => {
    const { numberFormat } = useContext(AccountingReportContext);

    const rows = useMemo(() => buildArAgingSummaryRows(data), [data]);
    const view = useMemo(() => buildArAgingSummaryView(), []);
    const tableMeta: ArAgingSummaryTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );
    const config = useMemo(
        () => buildArAgingSummaryConfig(numberFormat),
        [numberFormat],
    );

    return (
        <ViewTable
            data={rows}
            view={view}
            config={config}
            className={styles.arAgingSummaryTable}
            tableMeta={tableMeta}
            rowBorders={false}
            enableColumnResizing
        />
    );
};
